import {
    useEffect, useState
} from 'react';
import {
    useSearchParams, Navigate
} from 'react-router-dom';
import {
    fetchResources
} from './configApi';
import ChannelConfig from './components/ChannelConfig';
import InstructionConfig from './components/InstructionConfig';
import CollectionFiles from './components/CollectionFiles';
import AssistantChat from './components/AssistantChat';
import {
    Badge
} from 'shared/components/ui/badge';
import {
    Separator
} from 'shared/components/ui/separator';
import {
    Card
} from 'shared/components/ui/card';
import {
    cn
} from 'shared/lib/utils';
import coolriots from 'assets/coolriots.png';
import {
    Home, MessageSquare, Workflow, Database, Copy, Check
} from 'lucide-react';

function getCookie(name) {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? match[2] : '';
}

const NAV_ITEMS = [
    {
        key: 'overview',
        label: 'Overview',
        icon: Home,
        description: 'Dashboard & getting started',
        help: ''
    },
    {
        key: 'channels',
        label: 'Channels',
        icon: MessageSquare,
        description: 'Communication endpoints',
        help: 'Channels connect your assistant to platforms like Web Chat and WhatsApp, allowing users to interact with it.'
    },
    {
        key: 'instructions',
        label: 'Opcodes',
        icon: Workflow,
        description: 'Processing workflows',
        help: 'Opcodes define how your assistant processes queries — each contains a sequence of steps like LLM calls, API lookups, and knowledge base searches.'
    },
    {
        key: 'collections',
        label: 'Collections',
        icon: Database,
        description: 'Knowledge base files',
        help: 'Collections store documents your assistant uses as a knowledge base to provide accurate, grounded answers.'
    },
];

export default function ProvisionedConfig() {
    const [searchParams] = useSearchParams();
    const instanceId = searchParams.get('instance') || '';

    // If the broker baked `&key=<authKey>` into the dashboard URL (PDF Step 5
    // pattern), pick it up here, set the cookie, and strip it from the URL so
    // it doesn't sit in the address bar / browser history past load.
    const urlKey = searchParams.get('key') || '';
    if (urlKey) {
        document.cookie = `provisioned-config-key=${urlKey}; path=/; SameSite=None; Secure; Partitioned; max-age=${7 * 24 * 60 * 60}`;
        const cleanUrl = `${window.location.pathname}?instance=${encodeURIComponent(instanceId)}`;
        window.history.replaceState({}, '', cleanUrl);
    }

    const configKey = urlKey || getCookie('provisioned-config-key') || '';

    const [resources, setResources] = useState(null);
    const [active, setActive] = useState('overview');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [copiedField, setCopiedField] = useState(null);

    const [bounceToAuth, setBounceToAuth] = useState(false);

    useEffect(() => {
        if (!instanceId) {
            setLoading(false);
            return;
        }
        fetchResources(instanceId, configKey)
            .then(setResources)
            .catch(e => {
                // Stale or wrong cookie → silently bounce to the auth gate
                // instead of showing an error. The auth page lets the user
                // re-enter the key (or in the DE Studio flow, the parent's
                // "Open" button re-syncs the cookie before navigating).
                if (e?.status === 401 || /invalid config key|missing config key/i.test(e?.message || '')) {
                    // Match the SameSite=None; Secure; Partitioned attrs used at set time —
                    // otherwise the browser ignores the clear in cross-site iframe contexts.
                    document.cookie = 'provisioned-config-key=; path=/; SameSite=None; Secure; Partitioned; max-age=0';
                    setBounceToAuth(true);
                } else {
                    setError(e.message);
                }
            })
            .finally(() => setLoading(false));
    }, [configKey, instanceId]);

    // Redirect to auth page only after the backend rejects both platform
    // session auth and config-key auth.
    if (bounceToAuth) {
        return <Navigate to={`/provisioned-config/auth?instance=${instanceId}`} replace />;
    }

    if (loading) {
        return (
            <div className="flex h-screen items-center justify-center flex-col gap-3 bg-background">
                <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
                <p className="text-sm text-muted-foreground">Loading configuration...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex h-screen items-center justify-center flex-col gap-2">
                <h2 className="text-lg font-semibold">Unable to load configuration</h2>
                <p className="text-sm text-muted-foreground">{error}</p>
            </div>
        );
    }

    const counts = {
        channels:     resources?.channels?.length ?? 0,
        instructions: resources?.opcodes?.length ?? 0,
        collections:  resources?.collections?.length ?? 0,
    };

    const rawTitle = resources?.assistants?.[0]?.title || 'Assistant';
    const assistantTitle = rawTitle.replace(/\s*\(.*?\)\s*$/, '').replace(/-[a-f0-9]{6,}$/i, '').trim() || rawTitle;
    const assistantDescription = resources?.assistants?.[0]?.description || '';
    const activeItem = NAV_ITEMS.find(n => n.key === active);
    const copyValue = (key, value) => {
        navigator.clipboard?.writeText(value);
        setCopiedField(key);
        setTimeout(() => setCopiedField(null), 2000);
    };

    // A-0014: V2 assistant doc — primary key is `assistant_id` (Addendum §B), `_id` retired.
    const assistantDoc = resources?.assistants?.[0];
    const ASSISTANT_INFO = [
        {
            key: 'org',
            label: 'Organization ID',
            value: resources?.organizationId || ''
        },
        {
            key: 'suborg',
            label: 'Sub Organization ID',
            value: resources?.subOrganizationId || ''
        },
        {
            key: 'asst',
            label: 'Assistant ID',
            value: assistantDoc?.assistant_id || assistantDoc?._id || ''
        },
        {
            key: 'opcode',
            label: 'Default Opcode',
            value: assistantDoc?.default_opcode || ''
        },
        {
            key: 'persona',
            label: 'Persona',
            value: assistantDoc?.persona || ''
        },
        {
            key: 'status',
            label: 'Status',
            value: assistantDoc?.status || ''
        },
        {
            key: 'version',
            label: 'Version',
            value: assistantDoc?.version || ''
        },
    ].filter(i => i.value);

    const SUMMARY_CARDS = [
        {
            key: 'channels',
            label: 'Channels',
            icon: MessageSquare,
            color: 'text-[#7f1230] bg-[#b1092a]/10',
            count: counts.channels
        },
        {
            key: 'instructions',
            label: 'Opcodes',
            icon: Workflow,
            color: 'text-[#205493] bg-[#205493]/10',
            count: counts.instructions
        },
        {
            key: 'collections',
            label: 'Collections',
            icon: Database,
            color: 'text-[#6b5d12] bg-[#f4c430]/20',
            count: counts.collections
        },
    ];

    return (
        <div className="flex h-screen bg-[#f7f7f8] overflow-hidden" style={{
            fontFamily: "'Avenir', 'Futura', sans-serif"
        }}>
            {/* Sidebar */}
            <aside className="w-[232px] flex flex-col border-r border-[#e2e3e7] bg-white shrink-0">
                <div className="px-4 pt-4 pb-3">
                    <img src={coolriots} alt="CoolRIOTS" className="h-6" />
                    <h2 className="text-sm font-semibold mt-4 truncate text-[#141414]">{assistantTitle}</h2>
                    <p className="text-[11px] text-[#7c818a] mt-0.5">Configuration</p>
                </div>
                <Separator />
                <nav className="flex flex-col gap-1 p-2.5 pt-3">
                    {NAV_ITEMS.map(item => {
                        const Icon = item.icon;
                        const isActive = active === item.key;
                        return (
                            <button
                                key={item.key}
                                onClick={() => setActive(item.key)}
                                className={cn(
                                    'flex items-center gap-3 cursor-pointer rounded-lg px-3 py-2.5 text-left transition-colors',
                                    isActive
                                        ? 'bg-[#b1092a]/10 text-[#b1092a]'
                                        : 'text-[#4b4f58] hover:bg-[#f2f3f5] hover:text-[#141414]'
                                )}
                            >
                                <Icon className={cn('h-4 w-4 shrink-0', isActive ? 'text-[#b1092a]' : 'text-[#858a93]')} />
                                <div className="flex-1 min-w-0">
                                    <span className={cn('text-sm block', isActive ? 'font-semibold' : 'font-medium')}>{item.label}</span>
                                </div>
                                {item.key !== 'overview' && (
                                    <Badge variant="secondary" className="text-[10px] h-5 px-1.5 min-w-[20px] justify-center shrink-0 bg-[#f0f1f3] text-[#555a63]">
                                        {counts[item.key]}
                                    </Badge>
                                )}
                            </button>
                        );
                    })}
                </nav>
            </aside>

            {/* Main */}
            <main className="flex-1 flex flex-col overflow-hidden">
                <div className="px-7 py-5 border-b border-[#e2e3e7] bg-white">
                    <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                            <div className="flex items-center gap-2">
                                <h1 className="text-xl font-semibold text-[#141414] truncate">
                                    {active === 'overview' ? assistantTitle : activeItem.label}
                                </h1>
                                {active === 'overview' && (
                                    <span className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#e7f6ed] text-[#16794a]">
                                        <span className="h-1.5 w-1.5 rounded-full bg-[#1f9d5a]" />
                                        Active
                                    </span>
                                )}
                            </div>
                            <p className="text-sm text-[#6d7280] mt-1 truncate">
                                {active === 'overview' ? assistantDescription || 'Configuration portal' : activeItem.description}
                            </p>
                        </div>
                        {active !== 'overview' && (
                            <span className="text-sm text-[#858a93] shrink-0">
                                {counts[active]} {counts[active] !== 1 ? 'items' : 'item'}
                            </span>
                        )}
                    </div>
                </div>

                {/* Overview */}
                <div className={`flex-1 overflow-y-auto p-7 ${active === 'overview' ? '' : 'hidden'}`}>
                    <div className="max-w-5xl space-y-5">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {SUMMARY_CARDS.map(card => {
                                const Icon = card.icon;
                                return (
                                    <Card
                                        key={card.key}
                                        className="p-4 cursor-pointer hover:border-[#b1092a]/35 hover:shadow-sm transition-colors border-[#dfe1e6] bg-white"
                                        onClick={() => setActive(card.key)}
                                    >
                                        <div className="flex items-start justify-between gap-3">
                                            <div>
                                                <p className="text-[11px] uppercase tracking-wide text-[#858a93] font-semibold">{card.label}</p>
                                                <p className="text-3xl font-semibold text-[#141414] mt-2">{card.count}</p>
                                            </div>
                                            <div className={cn('h-9 w-9 rounded-lg flex items-center justify-center', card.color)}>
                                                <Icon className="h-4.5 w-4.5" />
                                            </div>
                                        </div>
                                    </Card>
                                );
                            })}
                        </div>

                        {ASSISTANT_INFO.length > 0 && (
                            <Card className="border-[#dfe1e6] bg-white overflow-hidden">
                                <div className="px-5 py-4 border-b border-[#edf0f2]">
                                    <h3 className="text-sm font-semibold text-[#141414]">Assistant Information</h3>
                                </div>
                                <div className="divide-y divide-[#edf0f2]">
                                    {ASSISTANT_INFO.map(item => (
                                        <div key={item.key} className="grid grid-cols-[160px_1fr_auto] items-center gap-4 px-5 py-3">
                                            <p className="text-xs font-medium text-[#6d7280]">{item.label}</p>
                                            <span className="text-sm font-mono text-[#343840] truncate">{item.value}</span>
                                            <button
                                                onClick={() => copyValue(item.key, item.value)}
                                                className="shrink-0 h-7 w-7 flex items-center justify-center rounded-md hover:bg-[#f0f1f3] transition-colors cursor-pointer"
                                                title={`Copy ${item.label}`}
                                            >
                                                {copiedField === item.key
                                                    ? <Check className="h-3.5 w-3.5 text-[#16794a]" />
                                                    : <Copy className="h-3.5 w-3.5 text-[#858a93]" />
                                                }
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </Card>
                        )}
                    </div>
                </div>

                <div className={`flex-1 overflow-y-auto p-6 ${active === 'channels' ? '' : 'hidden'}`}>
                    <ChannelConfig channels={resources?.channels} assistants={resources?.assistants ?? []} collections={resources?.collections ?? []} instanceId={instanceId} configKey={configKey} organizationId={resources?.organizationId} />
                </div>
                <div className={`flex-1 overflow-y-auto p-6 ${active === 'instructions' ? '' : 'hidden'}`}>
                    <InstructionConfig instructions={resources?.instructions} collections={resources?.collections ?? []} apis={resources?.apis ?? []} instanceId={instanceId} configKey={configKey} />
                </div>
                <div className={`flex-1 overflow-y-auto p-6 ${active === 'collections' ? '' : 'hidden'}`}>
                    <CollectionFiles collections={resources?.collections} instanceId={instanceId} configKey={configKey} organizationId={resources?.organizationId} />
                </div>
            </main>

            {/* A-0014: pass the full assistant doc so AssistantChat can build the typed-input form. */}
            <AssistantChat
                instanceId={instanceId}
                configKey={configKey}
                assistant={assistantDoc}
                opcodes={resources?.opcodes ?? []}
            />
        </div>
    );
}
